import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import {
  Card,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  message,
  Tabs,
  Table,
  Tag,
  Modal,
  Form,
  Upload,
  Spin,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../components/AgGridWithColumnState";
import ResizableGridContainer from "../Shared/Utils/ResizableGridContainer";
import { PlusOutlined, UploadOutlined, InboxOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useSelector, shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  getAllInvoicesParallel,
  getAllCreditsParallel,
  getCreditById,
  getInvoiceById,
  deleteInvoice,
  deleteCredit,
  uploadBillsFile,
  exportInvoicePDF,
  exportCreditPDF,
  approveInvoice,
  rejectInvoice,
  approveCredit,
  rejectCredit,
  getAccountsPayableStatistics,
  clearAccountingCache,
  // Quick first-page functions for hybrid loading
  getFirstPageInvoices,
  getFirstPageCreditsForBills,
} from "../../api/accounting";
import { clearOverpaymentCache } from "../../api/overpayment";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheKeys,
  CacheTTL,
} from "../../context/AccountingDataCache";
import { getBillColumns, getCreditNoteColumns } from "./agGridColumns";
import CreateInvoiceModal from "../Shared/Forms/CreateInvoiceModal";
import CreateCredit from "../Shared/Forms/CreateCredit";
import BillHistory from "../Shared/Modals/BillHistory";
import UpcomingBills from "../Reports/UpcomingBills";
import InvoiceViewModal from "../Shared/Modals/InvoiceViewModal";
import ApprovalModal from "../Shared/Modals/ApprovalModal";
import BatchUploadBillsInvoices from "../Shared/Utils/BatchUploadBillsInvoices";
import BatchEditModal from "../Shared/Modals/BatchEditModal";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../../../utils/secureExcelUtils";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Custom styles for the table
const tableStyles = `
  .recent-bills-table .ant-table-thead > tr > th {
    background-color: #fafafa;
    border-bottom: 1px solid #f0f0f0;
    font-weight: 500;
    padding: 6px 8px;
    font-size: 12px;
  }
  .recent-bills-table .ant-table-tbody > tr > td {
    padding: 6px 8px;
    border-bottom: 1px solid #f0f0f0;
    font-size: 12px;
  }
  .recent-bills-table .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5;
  }
  .recent-bills-table .ant-table {
    margin: 0;
  }
  .recent-bills-table .ant-table-container {
    border-radius: 4px;
  }
`;

const EMPTY_ARRAY = [];

const AccountsPayable = ({ selectedBrands: selectedBrandsProp = [] }) => {
  const profile = useSelector((state) => state.profile, shallowEqual);
  const profileBrands =
    useSelector(
      (state) => state.profile?.brands ?? EMPTY_ARRAY,
      shallowEqual
    ) ?? EMPTY_ARRAY;
  const navigate = useNavigate();

  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    displaySymbol,
    exchangeRate,
    isConverted,
    decimalPrecision,
  } = useDisplayCurrency();

  // Listen for global brand/client changes to invalidate cache and trigger refresh
  useEffect(() => {
    const handleGlobalBrandChanged = () => {
      // Invalidate all payable caches when brand changes
      invalidateCacheByPrefix("bills_");
      invalidateCacheByPrefix("bill_credits_");
      invalidateCacheByPrefix("bill_history_");
      invalidateCacheByPrefix("payable_statistics_");
    };

    const handleGlobalClientChanged = () => {
      // When client changes, brands will also change, so invalidate everything
      invalidateCacheByPrefix("bills_");
      invalidateCacheByPrefix("bill_credits_");
      invalidateCacheByPrefix("bill_history_");
      invalidateCacheByPrefix("payable_statistics_");
    };

    const handleBillCreated = () => {
      // Invalidate cache and refresh when a bill is created elsewhere
      invalidateCacheByPrefix("bills_");
      invalidateCacheByPrefix("bill_credits_");
      invalidateCacheByPrefix("bill_history_");
      invalidateCacheByPrefix("payable_statistics_");
      // Trigger refresh
      if (fetchBillsDataRef.current) fetchBillsDataRef.current(true);
      if (fetchCreditNotesDataRef.current) fetchCreditNotesDataRef.current(true);
      if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
      if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
    };

    const handleCreditNoteCreated = (event) => {
      // Only refresh if it's a bill credit (credit_type === "bill")
      const creditType = event?.detail?.creditType;
      if (creditType === "bill") {
        // Invalidate cache and refresh when a bill credit note is created elsewhere
        invalidateCacheByPrefix("bill_credits_");
        invalidateCacheByPrefix("bills_");
        invalidateCacheByPrefix("bill_history_");
        invalidateCacheByPrefix("payable_statistics_");
        // Trigger refresh
        if (fetchBillsDataRef.current) fetchBillsDataRef.current(true);
        if (fetchCreditNotesDataRef.current) fetchCreditNotesDataRef.current(true);
        if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
        if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
      }
    };

    window.addEventListener("globalBrandChanged", handleGlobalBrandChanged);
    window.addEventListener("globalClientChanged", handleGlobalClientChanged);
    window.addEventListener("billCreated", handleBillCreated);
    window.addEventListener("creditNoteCreated", handleCreditNoteCreated);

    return () => {
      window.removeEventListener(
        "globalBrandChanged",
        handleGlobalBrandChanged
      );
      window.removeEventListener(
        "globalClientChanged",
        handleGlobalClientChanged
      );
      window.removeEventListener("billCreated", handleBillCreated);
      window.removeEventListener("creditNoteCreated", handleCreditNoteCreated);
    };
  }, []);

  // Use useMemo to prevent selectedBrands from changing on every render
  const selectedBrands = useMemo(() => {
    let brands =
      selectedBrandsProp && selectedBrandsProp.length > 0
        ? selectedBrandsProp
        : profileBrands;
    return brands;
  }, [selectedBrandsProp, profileBrands]);

  // Create a stable key from selectedBrands to prevent unnecessary re-fetches
  const brandsKey = useMemo(() => {
    if (!selectedBrands || selectedBrands.length === 0) return "";
    return selectedBrands
      .map((b) => b.id)
      .sort()
      .join(",");
  }, [selectedBrands]);

  // CRITICAL: Check global cache IMMEDIATELY to restore data instantly on navigation
  const initialBillsCache = useMemo(() => {
    if (!brandsKey) return null;
    return getCachedData(CacheKeys.bills(brandsKey));
  }, [brandsKey]);

  const initialCreditsCache = useMemo(() => {
    if (!brandsKey) return null;
    return getCachedData(CacheKeys.billCredits(brandsKey));
  }, [brandsKey]);

  // Separate refs to allow parallel fetching of bills and credits
  const fetchingBillsRef = useRef(false);
  const fetchingCreditsRef = useRef(false);
  const lastFetchedBrandsKey = useRef("");
  const lastFetchedBillsBrandsKey = useRef(initialBillsCache ? brandsKey : "");
  const lastFetchedCreditsBrandsKey = useRef(
    initialCreditsCache ? brandsKey : ""
  );
  // Ref for debouncing refresh calls to prevent multiple rapid refreshes
  const refreshTimeoutRef = useRef(null);
  const cachedDataRef = useRef(null);
  const fetchBillsDataRef = useRef(null);
  const fetchCreditNotesDataRef = useRef(null);
  const fetchStatisticsRef = useRef(null);
  const fetchHistoryDataRef = useRef(null);

  // For components that need a single brand, use the first one
  const firstBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;

  // Fallback to sessionStorage if Redux profile not loaded yet
  const userRole = profile?.role || sessionStorage.getItem("user_role") || null;

  // Initialize state from cache - instant restore on navigation
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(() => initialBillsCache || []);
  const [creditNotesData, setCreditNotesData] = useState(
    () => initialCreditsCache || []
  );
  const [historyData, setHistoryData] = useState([]);
  const [statistics, setStatistics] = useState({
    totalOutstanding: 0,
    overdueAmount: 0,
    paidThisMonth: 0,
    totalBills: 0,
    totalCredits: 0,
    totalCreditNotes: 0,
    pendingPayments: 0,
    unallocatedCredits: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from localStorage
    return localStorage.getItem("accounts-payable-active-tab") || "dashboard";
  });
  // Track which tabs have been visited for caching purposes (but don't keep them mounted)
  const visitedTabsRef = useRef(new Set(["dashboard"]));
  const [createBillModalVisible, setCreateBillModalVisible] = useState(false);
  const [batchUploadBillsVisible, setBatchUploadBillsVisible] = useState(false);
  const [createCreditModalVisible, setCreateCreditModalVisible] =
    useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [editingCreditId, setEditingCreditId] = useState(null);
  const [viewInvoiceModalVisible, setViewInvoiceModalVisible] = useState(false);
  const [viewingInvoiceId, setViewingInvoiceId] = useState(null);
  const [viewingCreditId, setViewingCreditId] = useState(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const [paymentsModalVisible, setPaymentsModalVisible] = useState(false);
  const [selectedInvoiceForPayments, setSelectedInvoiceForPayments] =
    useState(null);

  // Approval modal state
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [selectedItemForApproval, setSelectedItemForApproval] = useState(null);
  const [approvalItemType, setApprovalItemType] = useState("invoice"); // 'invoice' or 'credit'

  // Bulk approval state
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [selectedCreditIds, setSelectedCreditIds] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [batchEditModalVisible, setBatchEditModalVisible] = useState(false);

  const billsGridApiRef = useRef(null);
  const creditsGridApiRef = useRef(null);

  // Background loading state for hybrid loading
  const [isLoadingMoreBills, setIsLoadingMoreBills] = useState(false);
  const [isLoadingMoreCredits, setIsLoadingMoreCredits] = useState(false);
  const backgroundBillsAbortRef = useRef(false);
  const backgroundCreditsAbortRef = useRef(false);

  // CRITICAL: Refs for preserving filter state across refreshes
  const savedBillsFilterRef = useRef(null);
  const savedCreditsFilterRef = useRef(null);
  const pendingBillsFilterRestoreRef = useRef(false);
  const pendingCreditsFilterRestoreRef = useRef(false);
  // CRITICAL: Refs for preserving sort state across refreshes
  const savedBillsSortRef = useRef(null);
  const savedCreditsSortRef = useRef(null);
  const pendingBillsSortRestoreRef = useRef(false);
  const pendingCreditsSortRestoreRef = useRef(false);

  useEffect(() => {
    if (billsGridApiRef.current) {
      billsGridApiRef.current.refreshCells({ force: true });
    }
  }, [selectedBillIds]);

  useEffect(() => {
    if (creditsGridApiRef.current) {
      creditsGridApiRef.current.refreshCells({ force: true });
    }
  }, [selectedCreditIds]);

  // Effect to restore bills filter AFTER data has loaded
  useEffect(() => {
    if (
      pendingBillsFilterRestoreRef.current &&
      !loading &&
      data.length > 0 &&
      savedBillsFilterRef.current &&
      Object.keys(savedBillsFilterRef.current).length > 0
    ) {
      const restoreFilters = (attempt = 1) => {
        setTimeout(() => {
          if (billsGridApiRef.current) {
            try {
              billsGridApiRef.current.setFilterModel(
                savedBillsFilterRef.current
              );
              pendingBillsFilterRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreFilters(attempt + 1);
              else pendingBillsFilterRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreFilters(attempt + 1);
          } else {
            pendingBillsFilterRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreFilters();
    }
  }, [loading, data.length]);

  // Effect to restore credits filter AFTER data has loaded
  useEffect(() => {
    if (
      pendingCreditsFilterRestoreRef.current &&
      !loading &&
      creditNotesData.length > 0 &&
      savedCreditsFilterRef.current &&
      Object.keys(savedCreditsFilterRef.current).length > 0
    ) {
      const restoreFilters = (attempt = 1) => {
        setTimeout(() => {
          if (creditsGridApiRef.current) {
            try {
              creditsGridApiRef.current.setFilterModel(
                savedCreditsFilterRef.current
              );
              pendingCreditsFilterRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreFilters(attempt + 1);
              else pendingCreditsFilterRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreFilters(attempt + 1);
          } else {
            pendingCreditsFilterRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreFilters();
    }
  }, [loading, creditNotesData.length]);

  // Effect to restore bills sort AFTER data has loaded
  useEffect(() => {
    if (
      pendingBillsSortRestoreRef.current &&
      !loading &&
      data.length > 0 &&
      savedBillsSortRef.current &&
      savedBillsSortRef.current.length > 0
    ) {
      const restoreSort = (attempt = 1) => {
        setTimeout(() => {
          if (billsGridApiRef.current) {
            try {
              billsGridApiRef.current.applyColumnState({
                state: savedBillsSortRef.current,
                defaultState: { sort: null },
              });
              pendingBillsSortRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreSort(attempt + 1);
              else pendingBillsSortRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreSort(attempt + 1);
          } else {
            pendingBillsSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreSort();
    }
  }, [loading, data.length]);

  // Effect to restore credits sort AFTER data has loaded
  useEffect(() => {
    if (
      pendingCreditsSortRestoreRef.current &&
      !loading &&
      creditNotesData.length > 0 &&
      savedCreditsSortRef.current &&
      savedCreditsSortRef.current.length > 0
    ) {
      const restoreSort = (attempt = 1) => {
        setTimeout(() => {
          if (creditsGridApiRef.current) {
            try {
              creditsGridApiRef.current.applyColumnState({
                state: savedCreditsSortRef.current,
                defaultState: { sort: null },
              });
              pendingCreditsSortRestoreRef.current = false;
            } catch (e) {
              if (attempt < 5) restoreSort(attempt + 1);
              else pendingCreditsSortRestoreRef.current = false;
            }
          } else if (attempt < 5) {
            restoreSort(attempt + 1);
          } else {
            pendingCreditsSortRestoreRef.current = false;
          }
        }, attempt * 150);
      };
      restoreSort();
    }
  }, [loading, creditNotesData.length]);

  // Refresh data function - MUST be defined before any handlers that use it
  // Debounced to prevent multiple rapid refreshes
  // CRITICAL: Preserves filter state across refreshes
  const handleRefresh = useCallback(() => {
    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // CRITICAL: Save current filter and sort state BEFORE refreshing
    if (billsGridApiRef.current) {
      try {
        const currentFilters = billsGridApiRef.current.getFilterModel();
        if (currentFilters && Object.keys(currentFilters).length > 0) {
          savedBillsFilterRef.current = currentFilters;
          pendingBillsFilterRestoreRef.current = true;
        }
        // Save sort state
        const columnState = billsGridApiRef.current.getColumnState();
        const sortState = columnState
          .filter((col) => col.sort !== null)
          .map((col) => ({
            colId: col.colId,
            sort: col.sort,
            sortIndex: col.sortIndex,
          }));
        if (sortState.length > 0) {
          savedBillsSortRef.current = sortState;
          pendingBillsSortRestoreRef.current = true;
        }
      } catch (e) {
        // Grid might not be ready, ignore
      }
    }
    if (creditsGridApiRef.current) {
      try {
        const currentFilters = creditsGridApiRef.current.getFilterModel();
        if (currentFilters && Object.keys(currentFilters).length > 0) {
          savedCreditsFilterRef.current = currentFilters;
          pendingCreditsFilterRestoreRef.current = true;
        }
        // Save sort state
        const columnState = creditsGridApiRef.current.getColumnState();
        const sortState = columnState
          .filter((col) => col.sort !== null)
          .map((col) => ({
            colId: col.colId,
            sort: col.sort,
            sortIndex: col.sortIndex,
          }));
        if (sortState.length > 0) {
          savedCreditsSortRef.current = sortState;
          pendingCreditsSortRestoreRef.current = true;
        }
      } catch (e) {
        // Grid might not be ready, ignore
      }
    }

    // Invalidate global cache for bills
    invalidateCacheByPrefix("bills_");
    invalidateCacheByPrefix("bill_credits_");
    invalidateCacheByPrefix("bill_history_");
    invalidateCacheByPrefix("ap-stats-");

    // Debounce the refresh by 300ms to batch multiple calls
    refreshTimeoutRef.current = setTimeout(() => {
      if (firstBrand) {
        if (fetchBillsDataRef.current) fetchBillsDataRef.current(true);
        if (fetchCreditNotesDataRef.current)
          fetchCreditNotesDataRef.current(true);
        if (fetchHistoryDataRef.current) fetchHistoryDataRef.current(true);
        if (fetchStatisticsRef.current) fetchStatisticsRef.current(true);
      }
    }, 300);
  }, [firstBrand]);

  // Date filter for dashboard statistics
  const [dashboardDateFilter, setDashboardDateFilter] = useState("this_month");
  const [dashboardCustomRange, setDashboardCustomRange] = useState(null);

  // AG Grid event handlers
  const onCellValueChanged = (params) => {
    // Determine which dataset to update based on current tab
    if (activeTab === "bills") {
      const newData = [...data];
      const rowIndex = newData.findIndex((row) => row.id === params.data.id);
      if (rowIndex !== -1) {
        newData[rowIndex] = {
          ...newData[rowIndex],
          [params.colDef.field]: params.newValue,
        };
        setData(newData);
      }
    } else if (activeTab === "creditNotes") {
      const newData = [...creditNotesData];
      const rowIndex = newData.findIndex((row) => row.id === params.data.id);
      if (rowIndex !== -1) {
        newData[rowIndex] = {
          ...newData[rowIndex],
          [params.colDef.field]: params.newValue,
        };
        // Force a new array reference to trigger AG Grid re-render
        setCreditNotesData([...newData]);
      }
    }
  };

  // Handle delete bill - with allocation check
  const handleDeleteBill = (id, billData) => {
    // Show delete confirmation
    Modal.confirm({
      title: "Delete Bill",
      content: `Are you sure you want to delete Bill #${
        billData?.number || id
      }?`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteInvoice(id);
          message.success("Bill deleted successfully");
          // Clear API cache before refresh to ensure fresh data
          clearAccountingCache();
          clearOverpaymentCache();
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error deleting bill:", error);
          const backendMsg =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null);

          // Check if the error is about allocations
          if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
            Modal.error({
              title: "Cannot Delete Bill - Has Allocations",
              content: (
                <div>
                  <p>{backendMsg}</p>
                  <Button
                    type="primary"
                    style={{ marginTop: 16 }}
                    onClick={() => {
                      Modal.destroyAll();
                      navigate(`/accounting/transactions-allocations`, {
                        state: {
                          invoiceId: id,
                          invoiceNumber: billData?.number,
                        },
                      });
                    }}
                  >
                    Go to Transactions & Allocations
                  </Button>
                </div>
              ),
            });
            return;
          }

          // Other errors
          Modal.error({
            title: "Cannot Delete Bill",
            content: backendMsg || "An error occurred while deleting the bill.",
          });
        }
      },
    });
  };

  // Old delete confirmation (not needed anymore)
  const handleDeleteBillOld = (id, billData) => {
    // Proceed with delete confirmation
    Modal.confirm({
      title: "Are you sure you want to delete this bill?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteInvoice(id);
          message.success("Bill deleted successfully");
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error deleting bill:", error);
          const backendMsg =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null);
          if (backendMsg) {
            Modal.warning({
              title: "Cannot delete bill",
              content: backendMsg,
            });
          } else {
            Modal.warning({
              title: "Cannot delete bill",
              content:
                "This document has allocations. Please unallocate first.",
            });
          }
        }
      },
    });
  };

  // Handle edit bill
  const handleEditBill = (id) => {
    setEditingBillId(id);
    setCreateBillModalVisible(true);
  };

  // Handle delete credit note - with allocation check
  const handleDeleteCredit = (id, creditData) => {
    // Show delete confirmation
    Modal.confirm({
      title: "Delete Credit Note",
      content: `Are you sure you want to delete Credit #${
        creditData?.number || id
      }?`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteCredit(id);
          message.success("Credit note deleted successfully");
          // Clear API cache before refresh to ensure fresh data
          clearAccountingCache();
          clearOverpaymentCache();
          // Use handleRefresh to preserve filter state
          handleRefresh();
        } catch (error) {
          console.error("Error deleting credit note:", error);
          const backendMsg =
            error?.response?.data?.detail ||
            error?.response?.data?.message ||
            (typeof error?.response?.data === "string"
              ? error.response.data
              : null);

          // Check if the error is about allocations
          if (backendMsg && backendMsg.toLowerCase().includes("allocation")) {
            Modal.error({
              title: "Cannot Delete Credit - Has Allocations",
              content: (
                <div>
                  <p>{backendMsg}</p>
                  <Button
                    type="primary"
                    style={{ marginTop: 16 }}
                    onClick={() => {
                      Modal.destroyAll();
                      navigate(`/accounting/transactions-allocations`, {
                        state: {
                          creditId: id,
                          creditNumber: creditData?.number,
                        },
                      });
                    }}
                  >
                    Go to Transactions & Allocations
                  </Button>
                </div>
              ),
            });
            return;
          }

          // Other errors
          Modal.error({
            title: "Cannot Delete Credit",
            content:
              backendMsg || "An error occurred while deleting the credit note.",
          });
        }
      },
    });
  };

  const handleBulkDeleteBills = useCallback(() => {
    if (selectedBillIds.length === 0) return;
    const selectedItems = selectedBillIds
      .map((id) => data.find((item) => item.id === id))
      .filter(Boolean);
    const hasAllocated = selectedItems.some(
      (item) =>
        parseFloat(item.allocated_amount || 0) > 0 ||
        (item.allocations && item.allocations.length > 0)
    );
    if (hasAllocated) {
      Modal.warning({
        title: "Cannot Delete - Some Bills Have Allocations",
        content:
          "Some selected bills have payments allocated. Please unallocate them first, or deselect them and try again.",
      });
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedBillIds.length} Bill(s)?`,
      content: `This will permanently delete ${selectedBillIds.length} selected bill(s). This action cannot be undone.`,
      okText: "Delete All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        for (const id of selectedBillIds) {
          try {
            await deleteInvoice(id);
            successCount++;
          } catch {
            failCount++;
          }
        }
        if (successCount > 0) {
          message.success(`Deleted ${successCount} bill(s)`);
          clearAccountingCache();
          clearOverpaymentCache();
          setSelectedBillIds([]);
          handleRefresh();
        }
        if (failCount > 0) {
          message.error(
            `Failed to delete ${failCount} bill(s) — they may have allocations.`
          );
        }
      },
    });
  }, [selectedBillIds, data, handleRefresh]);

  const handleBulkDeleteCredits = useCallback(() => {
    if (selectedCreditIds.length === 0) return;
    const selectedItems = selectedCreditIds
      .map((id) => creditNotesData.find((item) => item.id === id))
      .filter(Boolean);
    const hasAllocated = selectedItems.some(
      (item) =>
        parseFloat(item.allocated_amount || 0) > 0 ||
        (item.allocations && item.allocations.length > 0)
    );
    if (hasAllocated) {
      Modal.warning({
        title: "Cannot Delete - Some Credits Have Allocations",
        content:
          "Some selected credits have allocations. Please unallocate them first, or deselect them and try again.",
      });
      return;
    }
    Modal.confirm({
      title: `Delete ${selectedCreditIds.length} Credit(s)?`,
      content: `This will permanently delete ${selectedCreditIds.length} selected credit note(s). This action cannot be undone.`,
      okText: "Delete All",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        for (const id of selectedCreditIds) {
          try {
            await deleteCredit(id);
            successCount++;
          } catch {
            failCount++;
          }
        }
        if (successCount > 0) {
          message.success(`Deleted ${successCount} credit note(s)`);
          clearAccountingCache();
          clearOverpaymentCache();
          setSelectedCreditIds([]);
          handleRefresh();
        }
        if (failCount > 0) {
          message.error(
            `Failed to delete ${failCount} credit(s) — they may have allocations.`
          );
        }
      },
    });
  }, [selectedCreditIds, creditNotesData, handleRefresh]);

  // Handle edit credit note
  const handleEditCredit = (id) => {
    setEditingCreditId(id);
    setCreateCreditModalVisible(true);
  };

  // Handle view invoice/bill
  const handleViewInvoice = (id) => {
    setViewingInvoiceId(id);
    setViewInvoiceModalVisible(true);
    setViewingCreditId(null); // Ensure we're not viewing a credit
  };

  // Handle view credit note
  const handleViewCredit = (id) => {
    setViewingCreditId(id);
    setViewInvoiceModalVisible(true);
    setViewingInvoiceId(null); // Ensure we're not viewing an invoice
  };

  // Handle view payments modal
  const handleViewPayments = async (invoiceData) => {
    // If it's a credit and allocations are missing, fetch them
    if (
      invoiceData.credit_type &&
      (!invoiceData.allocations || invoiceData.allocations.length === 0)
    ) {
      try {
        const creditData = await getCreditById(invoiceData.id, true);
        // Update invoiceData with fetched allocations
        invoiceData = {
          ...invoiceData,
          allocations:
            creditData.credit_allocations || creditData.allocations || [],
          allocated_amount: creditData.allocated_amount || 0,
        };
      } catch (error) {
        console.error("Error fetching credit allocations:", error);
        message.error("Failed to load credit allocations");
      }
    }
    setSelectedInvoiceForPayments(invoiceData);
    setPaymentsModalVisible(true);
  };

  // Handle export PDF for bills
  const handleExportPDF = async (id) => {
    try {
      const response = await exportInvoicePDF(id);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bill_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      message.error("Failed to export PDF");
    }
  };

  // Handle export PDF for credit notes
  const handleExportCreditPDF = async (id) => {
    try {
      const response = await exportCreditPDF(id);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Credit_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting credit PDF:", error);
      message.error("Failed to export PDF");
    }
  };

  // Handle create bill success
  const handleCreateBillSuccess = () => {
    setCreateBillModalVisible(false);
    setEditingBillId(null);
    handleRefresh();
  };

  // Handle create credit success
  const handleCreateCreditSuccess = () => {
    setCreateCreditModalVisible(false);
    setEditingCreditId(null);
    handleRefresh();
  };

  // Handle approve invoice
  const handleApproveInvoice = async (invoiceId) => {
    try {
      await approveInvoice(invoiceId);
      message.success("Invoice approved successfully");
      handleRefresh();
    } catch (error) {
      console.error("Error approving invoice:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to approve invoice";
      message.error(errorMsg);
    }
  };

  // Handle reject invoice
  const handleRejectInvoice = async (invoiceId) => {
    Modal.confirm({
      title: "Reject Invoice",
      content: (
        <div>
          <p>Please provide a reason for rejection (optional):</p>
          <Input.TextArea
            id="rejection-reason-input"
            rows={4}
            placeholder="Enter rejection reason..."
            autoFocus
          />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const reasonInput = document.getElementById("rejection-reason-input");
        const rejectionReason = reasonInput?.value || "";
        try {
          await rejectInvoice(invoiceId, rejectionReason);
          message.success("Invoice rejected and reset to draft");
          handleRefresh();
        } catch (error) {
          console.error("Error rejecting invoice:", error);
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to reject invoice";
          message.error(errorMsg);
        }
      },
    });
  };

  // Handle open approval modal - fetch single invoice by ID for fast, reliable data
  const handleOpenApprovalModal = useCallback(
    async (item, itemType = "invoice") => {
      setApprovalItemType(itemType);

      try {
        let latestItem;
        if (itemType === "invoice") {
          // Fetch single bill by ID - much faster than fetching all
          latestItem = await getInvoiceById(item.id);
        } else {
          // Fetch single credit by ID
          latestItem = await getCreditById(item.id, true);
        }

        // Format it the same way as in fetchBillsData/fetchCreditNotesData
        if (itemType === "invoice") {
          const total = parseFloat(latestItem.total_amount || 0);
          const allocatedAmount = parseFloat(
            latestItem.allocated_amount || latestItem.paid_amount || 0
          );
          const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;
          let allocationStatus;
          if (percentPaid >= 99.99) {
            allocationStatus = {
              type: "fully_paid",
              label: "Fully Paid",
              color: "success",
            };
          } else if (percentPaid > 0) {
            allocationStatus = {
              type: "partially_paid",
              label: "Partially Paid",
              color: "warning",
            };
          } else {
            allocationStatus = {
              type: "unpaid",
              label: "Unpaid",
              color: "default",
            };
          }
          const formattedItem = {
            id: latestItem.id,
            number: latestItem.number || `BILL-${latestItem.id}`,
            partner: latestItem.partner?.name || "Unknown Vendor",
            invoice_type: "bill",
            issueDate: latestItem.issue_date,
            dueDate: latestItem.due_date,
            paidDate:
              latestItem.paid_date ||
              (latestItem.status === "paid" ? latestItem.updated_at : null),
            subtotal: latestItem.subtotal || 0,
            discount_amount: latestItem.discount_amount || 0,
            tax_rate: latestItem.tax_rate || 0,
            total: parseFloat(latestItem.total_amount || 0),
            paid_amount: parseFloat(latestItem.paid_amount || 0),
            allocated_amount: allocatedAmount,
            status: latestItem.status || "draft",
            approval_status: latestItem.approval_status || "draft",
            senior_approver: latestItem.senior_approver,
            senior_approval_date: latestItem.senior_approval_date,
            management_approver: latestItem.management_approver,
            management_approval_date: latestItem.management_approval_date,
            rejection_reason: latestItem.rejection_reason,
            rejected_by: latestItem.rejected_by,
            allocations: latestItem.allocations || [],
            items_count:
              latestItem.items_count ||
              (Array.isArray(latestItem.items) ? latestItem.items.length : 0),
            currency: latestItem.currency || "$",
            percentPaid,
            allocationStatus,
            last_modified_by: latestItem.last_modified_by || "Unknown",
            description: latestItem.notes || "",
            originalData: latestItem,
          };
          setSelectedItemForApproval(formattedItem);
        } else {
          const total = parseFloat(latestItem.total_amount || 0);
          const allocatedAmount = parseFloat(latestItem.allocated_amount || 0);
          const percentAllocated =
            total > 0 ? (allocatedAmount / total) * 100 : 0;
          let allocationStatus;
          if (percentAllocated >= 99.99) {
            allocationStatus = {
              type: "fully_allocated",
              label: "Fully Allocated",
              color: "success",
            };
          } else if (percentAllocated > 0) {
            allocationStatus = {
              type: "partially_allocated",
              label: "Partially Allocated",
              color: "warning",
            };
          } else {
            allocationStatus = {
              type: "unallocated",
              label: "Unallocated",
              color: "default",
            };
          }
          const remainingAmount = total - allocatedAmount;
          let creditStatus =
            remainingAmount <= 0.01
              ? "Fully Allocated"
              : remainingAmount < total
              ? "Partially Allocated"
              : "Draft";
          const formattedItem = {
            id: latestItem.id,
            number: latestItem.number || `CR-${latestItem.id}`,
            partner:
              latestItem.partner?.name ||
              latestItem.partner?.partner_name ||
              (typeof latestItem.partner === "string"
                ? latestItem.partner
                : "Unknown Vendor"),
            credit_type: latestItem.credit_type || "bill",
            issueDate: latestItem.issue_date,
            subtotal: latestItem.subtotal || 0,
            discount_amount: latestItem.discount_amount || 0,
            tax_rate: latestItem.tax_rate || 0,
            total: parseFloat(latestItem.total_amount || 0),
            allocated_amount: allocatedAmount,
            remaining_amount: remainingAmount,
            allocations: latestItem.credit_allocations || [],
            items_count:
              latestItem.items_count ||
              (Array.isArray(latestItem.items) ? latestItem.items.length : 0),
            currency: latestItem.currency || "$",
            percentAllocated,
            allocationStatus,
            status: creditStatus,
            approval_status: latestItem.approval_status || "draft",
            senior_approver: latestItem.senior_approver,
            senior_approval_date: latestItem.senior_approval_date,
            management_approver: latestItem.management_approver,
            management_approval_date: latestItem.management_approval_date,
            rejection_reason: latestItem.rejection_reason,
            rejected_by: latestItem.rejected_by,
            last_modified_by: latestItem.last_modified_by || "Unknown",
            originalData: latestItem,
          };
          setSelectedItemForApproval(formattedItem);
        }
      } catch (error) {
        console.error("Error fetching latest item for approval modal:", error);
        // Fallback to current item if fetch fails
        setSelectedItemForApproval(item);
      }

      setApprovalModalVisible(true);
    },
    []
  );

  // Handle approval modal success
  const handleApprovalSuccess = useCallback(() => {
    setApprovalModalVisible(false);
    setSelectedItemForApproval(null);
    // Refresh data to get updated approval status
    // Use a small delay to ensure the backend has processed the approval
    clearAccountingCache(); // Bust cache before refresh
    clearOverpaymentCache();
    // Use handleRefresh to preserve filter state - it handles the delay internally
    setTimeout(() => {
      handleRefresh();
    }, 500); // Small delay to ensure backend has saved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRefresh]);

  // Check if item is eligible for approval based on user role
  // Single-level management approval: management can approve drafts directly
  const isEligibleForApproval = useCallback(
    (item) => {
      const status = (item.approval_status || "draft").toLowerCase();
      if (!userRole) return false;

      const role = userRole.toLowerCase();
      if (role === "management") {
        return status === "draft" || status === "pending_senior_approval" || status === "pending_management_approval";
      }

      return false;
    },
    [userRole]
  );

  // Handle selection change for bills grid
  const handleBillSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelectedBillIds(selectedRows.map((row) => row.id));
  }, []);

  // Handle selection change for credits grid
  const handleCreditSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    setSelectedCreditIds(selectedRows.map((row) => row.id));
  }, []);

  // Toggle bill selection for approval
  const handleToggleBillSelection = useCallback((billId) => {
    setSelectedBillIds((prev) => {
      const isSelected = prev.includes(billId);
      if (isSelected) {
        return prev.filter((id) => id !== billId);
      } else {
        return [...prev, billId];
      }
    });
  }, []);

  // Toggle credit selection for approval
  const handleToggleCreditSelection = useCallback((creditId) => {
    setSelectedCreditIds((prev) => {
      const isSelected = prev.includes(creditId);
      if (isSelected) {
        return prev.filter((id) => id !== creditId);
      } else {
        return [...prev, creditId];
      }
    });
  }, []);

  // Get eligible count for display
  const eligibleBillCount = useMemo(() => {
    return selectedBillIds
      .map((id) => data.find((item) => item.id === id))
      .filter((item) => item && isEligibleForApproval(item)).length;
  }, [selectedBillIds, data, isEligibleForApproval]);

  const eligibleCreditCount = useMemo(() => {
    return selectedCreditIds
      .map((id) => creditNotesData.find((item) => item.id === id))
      .filter((item) => item && isEligibleForApproval(item)).length;
  }, [selectedCreditIds, creditNotesData, isEligibleForApproval]);

  // Check if there are any items that can be approved (not just selected)
  const hasApprovableBills = useMemo(() => {
    return data.some((item) => isEligibleForApproval(item));
  }, [data, isEligibleForApproval]);

  const hasApprovableCredits = useMemo(() => {
    return creditNotesData.some((item) => isEligibleForApproval(item));
  }, [creditNotesData, isEligibleForApproval]);

  const totalApprovableBills = useMemo(() => {
    return data.filter((item) => isEligibleForApproval(item)).length;
  }, [data, isEligibleForApproval]);

  const totalApprovableCredits = useMemo(() => {
    return creditNotesData.filter((item) => isEligibleForApproval(item)).length;
  }, [creditNotesData, isEligibleForApproval]);

  // Handle approve credit
  const handleApproveCredit = async (creditId) => {
    try {
      await approveCredit(creditId);
      message.success("Credit approved successfully");
      handleRefresh();
    } catch (error) {
      console.error("Error approving credit:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Failed to approve credit";
      message.error(errorMsg);
    }
  };

  // Handle reject credit
  const handleRejectCredit = async (creditId) => {
    Modal.confirm({
      title: "Reject Credit",
      content: (
        <div>
          <p>Please provide a reason for rejection (optional):</p>
          <Input.TextArea
            id="rejection-reason-credit-input"
            rows={4}
            placeholder="Enter rejection reason..."
            autoFocus
          />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        const reasonInput = document.getElementById(
          "rejection-reason-credit-input"
        );
        const rejectionReason = reasonInput?.value || "";
        try {
          await rejectCredit(creditId, rejectionReason);
          message.success("Credit rejected and reset to draft");
          handleRefresh();
        } catch (error) {
          console.error("Error rejecting credit:", error);
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to reject credit";
          message.error(errorMsg);
        }
      },
    });
  };

  // Handle upload bill — redirects to batch upload
  const showUploadModal = () => {
    setBatchUploadBillsVisible(true);
  };

  const handleUploadCancel = () => {
    setUploadModalVisible(false);
    setFileList([]);
    form.resetFields();
  };

  const handleUploadOk = async () => {
    try {
      const values = await form.validateFields();

      if (fileList.length === 0) {
        message.error("Please select a file to upload");
        return;
      }

      const fileObj = fileList[0];
      const fileExtension = fileObj.name.split(".").pop().toLowerCase();

      if (!["xlsx", "xls", "csv"].includes(fileExtension)) {
        message.error(
          "Only Excel (.xlsx, .xls) or CSV (.csv) files are allowed"
        );
        return;
      }

      setUploadLoading(true);

      const billsFileData = {
        brand: firstBrand ? firstBrand.id : null,
        notes: values.notes || "",
        document_type: "bill",
      };

      const response = await uploadBillsFile(billsFileData, fileObj);

      if (response.processing_results) {
        const {
          partners_created,
          invoices_created,
          message: resultMessage,
          error,
        } = response.processing_results;

        if (error) {
          message.warning(
            <div>
              <p>File was uploaded but there was an error processing it:</p>
              <p>{error}</p>
            </div>,
            8
          );
        } else {
          message.success(
            <div>
              <p>Bills file uploaded successfully!</p>
              <p>{resultMessage}</p>
              <ul>
                <li>Partners created/found: {partners_created}</li>
                <li>Bills created: {invoices_created}</li>
              </ul>
            </div>,
            6
          );
        }
      } else {
        message.success("Bills file uploaded successfully");
      }

      // Use handleRefresh to preserve filter state
      handleRefresh();
      setUploadModalVisible(false);
      setFileList([]);
      form.resetFields();
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessage = JSON.stringify(errorData);
        if (
          errorMessage.includes("Duplicate entry") &&
          errorMessage.includes("file_hash")
        ) {
          message.error(
            "This file has already been uploaded. Please upload a different file."
          );
        } else if (typeof errorData === "object") {
          Object.keys(errorData).forEach((key) => {
            message.error(`${key}: ${errorData[key]}`);
          });
        } else {
          message.error("Failed to upload bills file: " + errorData);
        }
      } else {
        message.error("Failed to upload bills file: " + error.message);
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      const isAllowedType =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.type === "text/csv";
      if (!isAllowedType) {
        message.error("You can only upload Excel or CSV files!");
      } else {
        setFileList([file]);
      }
      return false;
    },
    fileList,
  };

  // Export bills to Excel - directly downloads, respects AG Grid filters
  // Includes currency conversion when a non-USD currency is selected
  const exportBillsToExcel = useCallback(() => {
    try {
      const billsToExport = [];
      const gridApi = billsGridApiRef.current;

      if (gridApi) {
        const displayedRowCount = gridApi.getDisplayedRowCount();
        for (let i = 0; i < displayedRowCount; i++) {
          const node = gridApi.getDisplayedRowAtIndex(i);
          if (node && node.data) {
            const totalUSD = parseFloat(node.data.total || 0);
            const paidUSD = parseFloat(
              node.data.allocated_amount || node.data.paid_amount || 0
            );

            // Apply currency conversion if active
            const totalDisplay = isConverted
              ? totalUSD * exchangeRate
              : totalUSD;
            const paidDisplay = isConverted ? paidUSD * exchangeRate : paidUSD;

            const exportRow = {
              "Bill Number": node.data.number || "",
              Client: node.data.clientName || "N/A",
              Brand: node.data.brandName || "N/A",
              Vendor: node.data.partner || "",
              "Issue Date": node.data.issueDate
                ? dayjs(node.data.issueDate).format("YYYY-MM-DD")
                : "",
              "Due Date": node.data.dueDate
                ? dayjs(node.data.dueDate).format("YYYY-MM-DD")
                : "",
              [`Total Amount (${displayCurrency})`]:
                totalDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Paid Amount (${displayCurrency})`]: paidDisplay.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }
              ),
              Status: node.data.status || "",
              "Approval Status": node.data.approval_status || "",
              "Payment Status": node.data.allocationStatus?.label || "",
              "Display Currency": displayCurrency,
            };

            // Add original USD values if converted
            if (isConverted) {
              exportRow["Total Amount (USD)"] = totalUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Paid Amount (USD)"] = paidUSD.toLocaleString("en-US", {
                minimumFractionDigits: decimalPrecision,
                maximumFractionDigits: decimalPrecision,
              });
              exportRow[
                "Exchange Rate"
              ] = `1 USD = ${exchangeRate} ${displayCurrency}`;
            }

            billsToExport.push(exportRow);
          }
        }
      }

      if (billsToExport.length === 0) {
        message.warning("No bills to export (check your filters)");
        return;
      }

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const currencySuffix = isConverted ? `_${displayCurrency}` : "";
      secureExportToExcel(
        billsToExport,
        `bills_export${currencySuffix}_${timestamp}.xlsx`
      );
      message.success(`Exported ${billsToExport.length} bills successfully!`);
    } catch (error) {
      console.error("Error exporting bills to Excel:", error);
      message.error("Failed to export bills: " + error.message);
    }
  }, [isConverted, exchangeRate, displayCurrency]);

  // Export credit notes to Excel - directly downloads, respects AG Grid filters
  // Includes currency conversion when a non-USD currency is selected
  const exportCreditsToExcel = useCallback(() => {
    try {
      const creditsToExport = [];
      const creditsApi = creditsGridApiRef.current;

      if (creditsApi) {
        const displayedCreditCount = creditsApi.getDisplayedRowCount();
        for (let i = 0; i < displayedCreditCount; i++) {
          const node = creditsApi.getDisplayedRowAtIndex(i);
          if (node && node.data) {
            const totalUSD = parseFloat(node.data.total || 0);
            const allocatedUSD = parseFloat(node.data.allocated_amount || 0);
            const remainingUSD = parseFloat(node.data.remaining_amount || 0);

            // Apply currency conversion if active
            const totalDisplay = isConverted
              ? totalUSD * exchangeRate
              : totalUSD;
            const allocatedDisplay = isConverted
              ? allocatedUSD * exchangeRate
              : allocatedUSD;
            const remainingDisplay = isConverted
              ? remainingUSD * exchangeRate
              : remainingUSD;

            const exportRow = {
              "Credit Number": node.data.number || "",
              Client: node.data.clientName || "N/A",
              Brand: node.data.brandName || "N/A",
              Vendor: node.data.partner || "",
              "Issue Date": node.data.issueDate
                ? dayjs(node.data.issueDate).format("YYYY-MM-DD")
                : "",
              "Due Date": node.data.dueDate
                ? dayjs(node.data.dueDate).format("YYYY-MM-DD")
                : "",
              [`Total Amount (${displayCurrency})`]:
                totalDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Allocated Amount (${displayCurrency})`]:
                allocatedDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              [`Remaining Amount (${displayCurrency})`]:
                remainingDisplay.toLocaleString("en-US", {
                  minimumFractionDigits: decimalPrecision,
                  maximumFractionDigits: decimalPrecision,
                }),
              Status: node.data.status || "",
              "Approval Status": node.data.approval_status || "",
              "Allocation Status": node.data.allocationStatus?.label || "",
              "Display Currency": displayCurrency,
            };

            // Add original USD values if converted
            if (isConverted) {
              exportRow["Total Amount (USD)"] = totalUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Allocated Amount (USD)"] = allocatedUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow["Remaining Amount (USD)"] = remainingUSD.toLocaleString(
                "en-US",
                { minimumFractionDigits: decimalPrecision, maximumFractionDigits: decimalPrecision }
              );
              exportRow[
                "Exchange Rate"
              ] = `1 USD = ${exchangeRate} ${displayCurrency}`;
            }

            creditsToExport.push(exportRow);
          }
        }
      }

      if (creditsToExport.length === 0) {
        message.warning("No credit notes to export (check your filters)");
        return;
      }

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const currencySuffix = isConverted ? `_${displayCurrency}` : "";
      secureExportToExcel(
        creditsToExport,
        `credit_notes_export${currencySuffix}_${timestamp}.xlsx`
      );
      message.success(
        `Exported ${creditsToExport.length} credit notes successfully!`
      );
    } catch (error) {
      console.error("Error exporting credits to Excel:", error);
      message.error("Failed to export credit notes: " + error.message);
    }
  }, [isConverted, exchangeRate, displayCurrency]);

  // Format bill data helper
  const formatBill = useCallback((bill) => {
    const total = parseFloat(bill.total_amount || 0);
    const allocatedAmount = parseFloat(
      bill.allocated_amount || bill.paid_amount || 0
    );
    const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;

    let allocationStatus;
    if (percentPaid >= 99.99) {
      allocationStatus = {
        type: "fully_paid",
        label: "Fully Paid",
        color: "success",
      };
    } else if (percentPaid > 0) {
      allocationStatus = {
        type: "partially_paid",
        label: "Partially Paid",
        color: "warning",
      };
    } else {
      allocationStatus = {
        type: "unpaid",
        label: "Unpaid",
        color: "default",
      };
    }

    return {
      id: bill.id,
      number: bill.number || `BILL-${bill.id}`,
      partner: bill.partner?.name || "Unknown Vendor",
      clientName: bill.client_name || "N/A",
      brandName: bill.brand?.name || bill.brand_name || "N/A",
      brandId: bill.brand?.id || bill.brand_id || null,
      invoice_type: "bill",
      issueDate: bill.issue_date,
      dueDate: bill.due_date,
      paidDate:
        bill.paid_date || (bill.status === "paid" ? bill.updated_at : null),
      subtotal: bill.subtotal || 0,
      discount_amount: bill.discount_amount || 0,
      tax_rate: bill.tax_rate || 0,
      total: parseFloat(bill.total_amount || 0).toFixed(decimalPrecision),
      paid_amount: parseFloat(bill.paid_amount || 0),
      allocated_amount: allocatedAmount,
      status: bill.status || "draft",
      approval_status: bill.approval_status || "draft",
      senior_approver: bill.senior_approver,
      senior_approval_date: bill.senior_approval_date,
      management_approver: bill.management_approver,
      management_approval_date: bill.management_approval_date,
      rejection_reason: bill.rejection_reason,
      rejected_by: bill.rejected_by,
      allocations: bill.allocations || [],
      items_count:
        bill.items_count || (Array.isArray(bill.items) ? bill.items.length : 0),
      currency: bill.currency || "$",
      percentPaid,
      allocationStatus,
      last_modified_by: bill.last_modified_by || "Unknown",
      description: bill.notes || "",
      originalData: bill,
    };
  }, []);

  // Fetch bills data from API - HYBRID LOADING (fast first page, background rest)
  const fetchBillsData = useCallback(
    async (force = false) => {
      if (!selectedBrands || selectedBrands.length === 0) {
        return;
      }

      const cacheKey = CacheKeys.bills(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedBills = getCachedData(cacheKey);
        if (cachedBills) {
          setData(cachedBills);
          setPagination((prev) => ({
            ...prev,
            total: cachedBills.length,
          }));
          setLoading(false);
          fetchingBillsRef.current = false;
          lastFetchedBillsBrandsKey.current = brandsKey;
          return;
        }
      }

      // CRITICAL: Check if we already have data loaded for this EXACT brand combination
      if (
        !force &&
        data.length > 0 &&
        lastFetchedBillsBrandsKey.current === brandsKey
      ) {
        setLoading(false);
        fetchingBillsRef.current = false;
        return;
      }

      // Prevent duplicate simultaneous requests
      if (fetchingBillsRef.current && !force) {
        return;
      }

      // Abort any ongoing background loading
      backgroundBillsAbortRef.current = true;
      fetchingBillsRef.current = true;
      setLoading(true);

      const brandIds = selectedBrands.map((b) => b.id).join(",");

      try {
        // ========== PHASE 1: Quick first page load ==========
        const firstPageResponse = await getFirstPageInvoices({
          include_allocations: "true",
          brand: brandIds,
          invoice_type: "bill",
        });

        const firstPageBills = firstPageResponse?.results || [];
        const formattedFirstPage = firstPageBills.map((bill) =>
          formatBill(bill)
        );
        const sortedFirstPage = formattedFirstPage.sort((a, b) => {
          const dateA = dayjs(a.issueDate);
          const dateB = dayjs(b.issueDate);
          return dateB.diff(dateA);
        });

        // ========== SHOW FIRST PAGE IMMEDIATELY ==========
        setData(sortedFirstPage);
        setPagination((prev) => ({
          ...prev,
          total: firstPageResponse.totalCount || sortedFirstPage.length,
        }));
        setLoading(false);
        fetchingBillsRef.current = false;
        lastFetchedBillsBrandsKey.current = brandsKey;

        // ========== PHASE 2: Background load remaining data ==========
        if (firstPageResponse.hasMore) {
          backgroundBillsAbortRef.current = false;
          setIsLoadingMoreBills(true);

          // Use setTimeout to let the UI render first
          setTimeout(async () => {
            try {
              // Fetch ALL data
              const fullResponse = await getAllInvoicesParallel({
                include_allocations: true,
                brand: brandIds,
                invoice_type: "bill",
              });

              // Check if we should abort
              if (backgroundBillsAbortRef.current) {
                setIsLoadingMoreBills(false);
                return;
              }

              const allBills = fullResponse?.results || [];
              const formattedAll = allBills.map((bill) => formatBill(bill));
              const sortedAll = formattedAll.sort((a, b) => {
                const dateA = dayjs(a.issueDate);
                const dateB = dayjs(b.issueDate);
                return dateB.diff(dateA);
              });

              // Check abort again before updating state
              if (backgroundBillsAbortRef.current) {
                setIsLoadingMoreBills(false);
                return;
              }

              // Update state with full data
              setData(sortedAll);
              setPagination((prev) => ({ ...prev, total: sortedAll.length }));

              // Cache the full data
              setCachedData(cacheKey, sortedAll, CacheTTL.MEDIUM);
            } catch (err) {
              console.error("Background bills loading error:", err);
            } finally {
              setIsLoadingMoreBills(false);
            }
          }, 100);
        } else {
          // No more data - cache what we have
          setCachedData(cacheKey, sortedFirstPage, CacheTTL.MEDIUM);
        }
      } catch {
        message.error("Failed to load bills");
        setData([]);
        setLoading(false);
        fetchingBillsRef.current = false;
      }
    },
    [selectedBrands, brandsKey, formatBill]
  );

  // Format credit data helper
  const formatCredit = useCallback((credit) => {
    const total = parseFloat(credit.total_amount || 0);
    const allocatedAmount = parseFloat(credit.allocated_amount || 0);
    const percentAllocated = total > 0 ? (allocatedAmount / total) * 100 : 0;

    let allocationStatus;
    if (percentAllocated >= 99.99) {
      allocationStatus = {
        type: "fully_allocated",
        label: "Fully Allocated",
        color: "success",
      };
    } else if (percentAllocated > 0) {
      allocationStatus = {
        type: "partially_allocated",
        label: "Partially Allocated",
        color: "warning",
      };
    } else {
      allocationStatus = {
        type: "unallocated",
        label: "Unallocated",
        color: "default",
      };
    }

    const remainingAmount = total - allocatedAmount;
    let creditStatus;
    if (remainingAmount <= 0.01) {
      creditStatus = "Fully Allocated";
    } else if (remainingAmount < total) {
      creditStatus = "Partially Allocated";
    } else {
      creditStatus = "Draft";
    }

    return {
      id: credit.id,
      number: credit.number || `CR-${credit.id}`,
      partner:
        credit.partner?.name ||
        credit.partner?.partner_name ||
        (typeof credit.partner === "string"
          ? credit.partner
          : "Unknown Vendor"),
      clientName: credit.client_name || "N/A",
      brandName: credit.brand?.name || credit.brand_name || "N/A",
      brandId: credit.brand?.id || credit.brand_id || null,
      credit_type: credit.credit_type || "bill",
      issueDate: credit.issue_date,
      dueDate: credit.due_date || null,
      subtotal: credit.subtotal || 0,
      discount_amount: credit.discount_amount || 0,
      tax_rate: credit.tax_rate || 0,
      total: parseFloat(credit.total_amount || 0),
      allocated_amount: parseFloat(credit.allocated_amount || 0),
      remaining_amount: remainingAmount,
      allocations: credit.credit_allocations || [],
      items_count:
        credit.items_count ||
        (Array.isArray(credit.items) ? credit.items.length : 0),
      currency: credit.currency || "$",
      percentAllocated,
      allocationStatus,
      status: creditStatus,
      approval_status: credit.approval_status || "draft",
      senior_approver: credit.senior_approver,
      senior_approval_date: credit.senior_approval_date,
      management_approver: credit.management_approver,
      management_approval_date: credit.management_approval_date,
      rejection_reason: credit.rejection_reason,
      rejected_by: credit.rejected_by,
      last_modified_by: credit.last_modified_by || "Unknown",
      originalData: credit,
    };
  }, []);

  // Fetch credit notes data from API - HYBRID LOADING (fast first page, background rest)
  const fetchCreditNotesData = useCallback(
    async (force = false) => {
      if (!selectedBrands || selectedBrands.length === 0) {
        return;
      }

      const cacheKey = CacheKeys.billCredits(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedCredits = getCachedData(cacheKey);
        if (cachedCredits) {
          setCreditNotesData(cachedCredits);
          setLoading(false);
          fetchingCreditsRef.current = false;
          lastFetchedCreditsBrandsKey.current = brandsKey;
          return;
        }
      }

      // CRITICAL: Check if we already have data loaded for this EXACT brand combination
      if (
        !force &&
        creditNotesData.length > 0 &&
        lastFetchedCreditsBrandsKey.current === brandsKey
      ) {
        setLoading(false);
        fetchingCreditsRef.current = false;
        return;
      }

      // Prevent duplicate simultaneous requests
      if (fetchingCreditsRef.current && !force) {
        return;
      }

      // Abort any ongoing background loading
      backgroundCreditsAbortRef.current = true;
      fetchingCreditsRef.current = true;
      setLoading(true);

      const brandIds = selectedBrands.map((b) => b.id).join(",");

      try {
        // ========== PHASE 1: Quick first page load ==========
        const firstPageResponse = await getFirstPageCreditsForBills({
          include_allocations: "true",
          brand: brandIds,
          credit_type: "bill",
        });

        const firstPageCredits = firstPageResponse?.results || [];
        const formattedFirstPage = firstPageCredits.map((credit) =>
          formatCredit(credit)
        );
        const sortedFirstPage = formattedFirstPage.sort((a, b) => {
          const dateA = dayjs(a.issueDate);
          const dateB = dayjs(b.issueDate);
          return dateB.diff(dateA);
        });

        // ========== SHOW FIRST PAGE IMMEDIATELY ==========
        setCreditNotesData(sortedFirstPage);
        setLoading(false);
        fetchingCreditsRef.current = false;
        lastFetchedCreditsBrandsKey.current = brandsKey;

        // ========== PHASE 2: Background load remaining data ==========
        if (firstPageResponse.hasMore) {
          backgroundCreditsAbortRef.current = false;
          setIsLoadingMoreCredits(true);

          // Use setTimeout to let the UI render first
          setTimeout(async () => {
            try {
              // Fetch ALL data
              const fullResponse = await getAllCreditsParallel({
                include_allocations: true,
                brand: brandIds,
                credit_type: "bill",
              });

              // Check if we should abort
              if (backgroundCreditsAbortRef.current) {
                setIsLoadingMoreCredits(false);
                return;
              }

              const allCredits =
                fullResponse?.results ||
                fullResponse?.data ||
                fullResponse ||
                [];
              const creditArray = Array.isArray(allCredits) ? allCredits : [];
              const formattedAll = creditArray.map((credit) =>
                formatCredit(credit)
              );
              const sortedAll = formattedAll.sort((a, b) => {
                const dateA = dayjs(a.issueDate);
                const dateB = dayjs(b.issueDate);
                return dateB.diff(dateA);
              });

              // Check abort again before updating state
              if (backgroundCreditsAbortRef.current) {
                setIsLoadingMoreCredits(false);
                return;
              }

              // Update state with full data
              setCreditNotesData(sortedAll);

              // Cache the full data
              setCachedData(cacheKey, sortedAll, CacheTTL.MEDIUM);
            } catch (err) {
              console.error("Background credits loading error:", err);
            } finally {
              setIsLoadingMoreCredits(false);
            }
          }, 100);
        } else {
          // No more data - cache what we have
          setCachedData(cacheKey, sortedFirstPage, CacheTTL.MEDIUM);
        }
      } catch {
        message.error("Failed to load credit notes");
        setCreditNotesData([]);
        setLoading(false);
        fetchingCreditsRef.current = false;
      }
    },
    [selectedBrands, brandsKey, formatCredit]
  );

  // Get date range based on filter
  const getDateRangeFromFilter = useCallback(
    (filter) => {
      const today = dayjs();
      let start, end;

      switch (filter) {
        case "this_month":
          start = today.startOf("month");
          end = today.endOf("month");
          break;
        case "last_month":
          start = today.subtract(1, "month").startOf("month");
          end = today.subtract(1, "month").endOf("month");
          break;
        case "this_year":
          start = today.startOf("year");
          end = today.endOf("month"); // YTD = year start to today
          break;
        case "custom":
          if (dashboardCustomRange && dashboardCustomRange.length === 2) {
            start = dashboardCustomRange[0];
            end = dashboardCustomRange[1];
          } else {
            start = today.startOf("month");
            end = today.endOf("month");
          }
          break;
        default:
          start = today.startOf("month");
          end = today.endOf("month");
      }

      return { start, end };
    },
    [dashboardCustomRange]
  );

  // Calculate statistics from actual data
  const fetchStatistics = useCallback(
    async (force = false) => {
      if (!brandsKey) return;

      const { start, end } = getDateRangeFromFilter(dashboardDateFilter);
      const cacheKey = `ap-stats-${brandsKey}-${start.format(
        "YYYY-MM-DD"
      )}-${end.format("YYYY-MM-DD")}`;

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedStats = getCachedData(cacheKey);
        if (cachedStats) {
          setStatistics(cachedStats);
          return;
        }
      }

      try {
        setLoading(true);
        const stats = await getAccountsPayableStatistics({
          brand_ids: brandsKey.split(","),
          start_date: start.format("YYYY-MM-DD"),
          end_date: end.format("YYYY-MM-DD"),
        });

        // Map backend response to frontend state
        // Backend returns total_unpaid_balance, map it to totalOutstanding
        // Handle both snake_case (backend) and camelCase (legacy) formats
        const totalUnpaid =
          stats?.total_unpaid_balance !== undefined
            ? stats.total_unpaid_balance
            : stats?.totalOutstanding !== undefined
            ? stats.totalOutstanding
            : 0;

        const newStatistics = {
          totalOutstanding: Number(totalUnpaid) || 0, // Ensure it's a number
          overdueAmount: Number(stats?.overdueAmount) || 0,
          paidThisMonth: Number(stats?.paidThisMonth) || 0,
          issuedInPeriod: Number(stats?.issuedInPeriod) || 0,
          creditIssuedInPeriod: Number(stats?.creditIssuedInPeriod) || 0,
          totalBills: Number(stats?.totalBills) || 0,
          totalCredits: Number(stats?.totalCredits) || 0,
          totalCreditNotes: Number(stats?.totalCredits) || 0, // Use the same value
          pendingPayments: Number(stats?.pendingPayments) || 0,
          unallocatedCredits: Number(stats?.unallocatedCredits) || 0,
        };

        setStatistics(newStatistics);

        // Cache in GLOBAL cache (5 minute TTL)
        setCachedData(cacheKey, newStatistics, CacheTTL.MEDIUM);
      } catch {
        message.error("Could not load dashboard statistics.");
      } finally {
        setLoading(false);
      }
    },
    [brandsKey, dashboardDateFilter, getDateRangeFromFilter]
  );

  // Fetch history data
  const fetchHistoryData = useCallback(
    async (force = false) => {
      if (!firstBrand) {
        return;
      }

      const cacheKey = CacheKeys.billHistory(brandsKey);

      // Check GLOBAL cache first if not forcing refresh
      if (!force) {
        const cachedHistory = getCachedData(cacheKey);
        if (cachedHistory) {
          setHistoryData(cachedHistory);
          return;
        }
      }

      try {
        const filters = {
          brand: firstBrand.id,
          invoice_type: "bill",
          start_date: dayjs().subtract(90, "day").format("YYYY-MM-DD"),
          end_date: dayjs().format("YYYY-MM-DD"),
        };

        // Use getAllInvoicesParallel which now fetches all pages automatically
        const response = await getAllInvoicesParallel(filters);

        if (response && response.results) {
          const formattedData = response.results.map((bill, index) => {
            const total = parseFloat(bill.total_amount || 0);
            const allocatedAmount = parseFloat(
              bill.allocated_amount || bill.paid_amount || 0
            );
            const percentPaid = total > 0 ? (allocatedAmount / total) * 100 : 0;

            let allocationStatus;
            if (percentPaid >= 99.99) {
              allocationStatus = {
                type: "fully_paid",
                label: "Fully Paid",
                color: "success",
              };
            } else if (percentPaid > 0) {
              allocationStatus = {
                type: "partially_paid",
                label: "Partially Paid",
                color: "warning",
              };
            } else {
              allocationStatus = {
                type: "unpaid",
                label: "Unpaid",
                color: "default",
              };
            }

            return {
              id: bill.id,
              sl: index + 1,
              number: bill.number || `BILL-${bill.id}`,
              partner: bill.partner?.name || "Unknown Vendor",
              invoice_type: "bill",
              billDate: bill.issue_date
                ? dayjs(bill.issue_date).format("MMM DD, YYYY")
                : "N/A",
              issueDate: bill.issue_date,
              dueDate: bill.due_date,
              paidDate: bill.paid_date,
              subtotal: bill.subtotal || 0,
              discount_amount: bill.discount_amount || 0,
              tax_rate: bill.tax_rate || 0,
              total: parseFloat(bill.total_amount || 0).toFixed(decimalPrecision),
              paid_amount: parseFloat(bill.paid_amount || 0),
              status: bill.status || getBillStatus(bill),
              approval_status: bill.approval_status || "draft", // Include approval_status
              allocations: bill.allocations || [], // Use allocations (includes payment, credit, cash, overpayment)
              items_count:
                bill.items_count ||
                (Array.isArray(bill.items) ? bill.items.length : 0),
              currency: bill.currency || "$",
              percentPaid,
              allocationStatus,
              last_modified_by: bill.last_modified_by || "Unknown",
              description: bill.description || bill.notes || "Bill payment",
              createdAt: bill.created_at
                ? dayjs(bill.created_at).format("MMM DD, YYYY HH:mm")
                : "N/A",
              originalData: bill,
            };
          });

          setHistoryData(formattedData);

          // Cache in GLOBAL cache (5 minute TTL)
          setCachedData(cacheKey, formattedData, CacheTTL.MEDIUM);
        } else {
          setHistoryData([]);
        }
      } catch {
        message.error("Failed to load history data");
        setHistoryData([]);
      }
    },
    [firstBrand, brandsKey]
  );

  // Keep refs updated
  useEffect(() => {
    fetchBillsDataRef.current = fetchBillsData;
    fetchCreditNotesDataRef.current = fetchCreditNotesData;
    fetchHistoryDataRef.current = fetchHistoryData;
    fetchStatisticsRef.current = fetchStatistics;
  }, [fetchBillsData, fetchCreditNotesData, fetchHistoryData, fetchStatistics]);

  // Determine bill status based on payment and due date
  const getBillStatus = (bill) => {
    const totalAmount = parseFloat(bill.total_amount || 0);
    const paidAmount = parseFloat(
      bill.allocated_amount || bill.paid_amount || 0
    );
    const dueDate = dayjs(bill.due_date);
    const today = dayjs();

    if (paidAmount >= totalAmount * 0.99) {
      return "Paid";
    } else if (dueDate.isBefore(today, "day")) {
      return "Overdue";
    } else {
      return "Pending";
    }
  };

  // Determine credit note status based on allocation
  const getCreditStatus = (credit) => {
    const remainingAmount = parseFloat(credit.remaining_amount || 0);
    const totalAmount = parseFloat(credit.total_amount || 0);

    if (remainingAmount <= 0.01) {
      return "Fully Allocated";
    } else if (remainingAmount < totalAmount) {
      return "Partially Allocated";
    } else {
      return "Unallocated";
    }
  };

  // Load data on component mount and when brand changes - with guards to prevent duplicate calls
  useEffect(() => {
    if (!brandsKey || brandsKey === "") {
      setData([]);
      setCreditNotesData([]);
      setHistoryData([]);
      lastFetchedBrandsKey.current = "";
      return;
    }

    // CRITICAL: Check GLOBAL cache immediately - if we have cached data, don't refetch
    const billsCacheKey = CacheKeys.bills(brandsKey);
    const creditsCacheKey = CacheKeys.billCredits(brandsKey);
    const historyCacheKey = CacheKeys.billHistory(brandsKey);
    const cachedBills = getCachedData(billsCacheKey);
    const cachedCredits = getCachedData(creditsCacheKey);
    const cachedHistory = getCachedData(historyCacheKey);

    // RESTORE FROM CACHE IMMEDIATELY - no refetch needed!
    if (cachedBills) {
      setData(cachedBills);
      lastFetchedBillsBrandsKey.current = brandsKey;
    }

    if (cachedCredits) {
      setCreditNotesData(cachedCredits);
      lastFetchedCreditsBrandsKey.current = brandsKey;
    }

    if (cachedHistory) {
      setHistoryData(cachedHistory);
    }

    // Small delay to batch multiple rapid changes
    const timeoutId = setTimeout(() => {
      // Only fetch if NOT already cached
      if (!cachedBills && fetchBillsDataRef.current)
        fetchBillsDataRef.current();
      if (!cachedCredits && fetchCreditNotesDataRef.current)
        fetchCreditNotesDataRef.current();
      if (!cachedHistory && fetchHistoryDataRef.current)
        fetchHistoryDataRef.current();
      if (fetchStatisticsRef.current) fetchStatisticsRef.current();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [brandsKey]);

  // Fetch / recalculate statistics when filters or selected brands change.
  // This is the single source of truth for loading dashboard stats to avoid
  // duplicate requests on mount or when the user role changes.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (fetchStatisticsRef.current) {
        fetchStatisticsRef.current();
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [brandsKey, dashboardDateFilter, dashboardCustomRange]);

  // AG Grid column definitions - recalculate when data or userRole changes
  const billColumns = useMemo(
    () => getBillColumns({}, userRole, data),
    [userRole, data]
  );
  const creditNoteColumns = useMemo(
    () => getCreditNoteColumns({}, userRole, creditNotesData),
    [userRole, creditNotesData]
  );

  const onBillsGridReady = useCallback((params) => {
    billsGridApiRef.current = params.api;
  }, []);

  const onCreditsGridReady = useCallback((params) => {
    creditsGridApiRef.current = params.api;
  }, []);

  // Resize Actions column when data changes to reflect dynamic width
  useEffect(() => {
    if (billsGridApiRef.current && billColumns) {
      const actionsCol = billColumns.find((col) => col.field === "actions");
      if (actionsCol && actionsCol.width) {
        // Small delay to ensure columns are updated
        setTimeout(() => {
          try {
            if (billsGridApiRef.current) {
              const column = billsGridApiRef.current.getColumn("actions");
              if (
                column &&
                typeof billsGridApiRef.current.setColumnWidth === "function"
              ) {
                billsGridApiRef.current.setColumnWidth(
                  "actions",
                  actionsCol.width,
                  true
                );
              }
            }
          } catch (error) {
            console.warn("Error setting column width for bills grid:", error);
          }
        }, 100);
      }
    }
  }, [billColumns, data]);

  useEffect(() => {
    if (creditsGridApiRef.current && creditNoteColumns) {
      const actionsCol = creditNoteColumns.find(
        (col) => col.field === "actions"
      );
      if (actionsCol && actionsCol.width) {
        // Small delay to ensure columns are updated
        setTimeout(() => {
          try {
            if (creditsGridApiRef.current) {
              const column = creditsGridApiRef.current.getColumn("actions");
              if (
                column &&
                typeof creditsGridApiRef.current.setColumnWidth === "function"
              ) {
                creditsGridApiRef.current.setColumnWidth(
                  "actions",
                  actionsCol.width,
                  true
                );
              }
            }
          } catch (error) {
            console.warn("Error setting column width for credits grid:", error);
          }
        }, 100);
      }
    }
  }, [creditNoteColumns, creditNotesData]);

  // AG Grid context to pass handlers to cell renderers
  const gridContext = useMemo(
    () => ({
      handleViewInvoice,
      handleEditInvoice: handleEditBill,
      handleDeleteInvoice: handleDeleteBill,
      handleExportPDF,
      handleViewCredit,
      handleEditCredit,
      handleDeleteCredit,
      handleExportCreditPDF,
      handleViewPayments,
      handleApproveInvoice,
      handleRejectInvoice,
      handleApproveCredit,
      handleRejectCredit,
      handleOpenApprovalModal,
      handleToggleBillSelection,
      handleToggleCreditSelection,
      selectedBillIds,
      selectedCreditIds,
      userRole,
    }),
    [
      handleViewInvoice,
      handleViewPayments,
      handleEditBill,
      handleDeleteBill,
      handleExportPDF,
      handleExportCreditPDF,
      handleViewCredit,
      handleEditCredit,
      handleDeleteCredit,
      handleApproveInvoice,
      handleRejectInvoice,
      handleApproveCredit,
      handleRejectCredit,
      handleOpenApprovalModal,
      handleToggleBillSelection,
      handleToggleCreditSelection,
      selectedBillIds,
      selectedCreditIds,
      userRole,
    ]
  );

  // Default column definition for editable tables (Main Bills & Credits tabs)
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      editable: false,
      suppressMovable: false, // Allow column moving
      // Enable AND/OR conditions for text filters
      filterParams: {
        suppressAndOrCondition: false,
        buttons: ["apply", "reset"],
      },
    }),
    []
  );

  // Read-only column definition for dashboard tables
  const defaultColDefReadOnly = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
      editable: false, // Dashboard tables should be read-only
      suppressMovable: false,
      // Enable AND/OR conditions for text filters
      filterParams: {
        suppressAndOrCondition: false,
        buttons: ["apply", "reset"],
      },
    }),
    []
  );

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "Pending" },
      overdue: { color: "red", text: "Overdue" },
      paid: { color: "green", text: "Paid" },
      "fully allocated": { color: "green", text: "Fully Allocated" },
      "partially allocated": { color: "blue", text: "Partially Allocated" },
      unallocated: { color: "orange", text: "Unallocated" },
    };

    const config = statusConfig[status.toLowerCase()] || {
      color: "default",
      text: status,
    };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    // You can add pagination logic here if needed
  };

  // Bulk approve handler - defined after fetch functions
  const handleBulkApprove = useCallback(
    async (itemType = "both", useSelectedOnly = false) => {
      let itemsToApprove = [];

      if (itemType === "invoice" || itemType === "both") {
        const sourceData = data;
        const hasSelection = selectedBillIds.length > 0;
        // If useSelectedOnly and there are selected bills, use only selected eligible items
        const eligibleBills = sourceData.filter((item) => {
          const isEligible = isEligibleForApproval(item);
          if (useSelectedOnly && hasSelection) {
            return isEligible && selectedBillIds.includes(item.id);
          }
          return isEligible;
        });
        itemsToApprove.push(
          ...eligibleBills.map((item) => ({ ...item, type: "invoice" }))
        );
      }

      if (itemType === "credit" || itemType === "both") {
        const sourceData = creditNotesData;
        const hasSelection = selectedCreditIds.length > 0;
        // If useSelectedOnly and there are selected credits, use only selected eligible items
        const eligibleCredits = sourceData.filter((item) => {
          const isEligible = isEligibleForApproval(item);
          if (useSelectedOnly && hasSelection) {
            return isEligible && selectedCreditIds.includes(item.id);
          }
          return isEligible;
        });
        itemsToApprove.push(
          ...eligibleCredits.map((item) => ({ ...item, type: "credit" }))
        );
      }

      if (itemsToApprove.length === 0) {
        message.warning("No eligible items to approve");
        return;
      }

      // Show confirmation modal
      const isSelectedMode =
        useSelectedOnly &&
        (selectedBillIds.length > 0 || selectedCreditIds.length > 0);
      Modal.confirm({
        title: isSelectedMode ? "Approve Selected Items" : "Bulk Approve Items",
        content: `Are you sure you want to approve ${
          itemsToApprove.length
        } item${itemsToApprove.length > 1 ? "s" : ""}?`,
        okText: isSelectedMode ? "Approve Selected" : "Approve All",
        okType: "primary",
        cancelText: "Cancel",
        onOk: async () => {
          setBulkApproving(true);
          const results = {
            success: [],
            failed: [],
          };

          // Clear cache before starting bulk approval
          clearAccountingCache();
          clearOverpaymentCache();

          // Process all items with Promise.allSettled
          const promises = itemsToApprove.map(async (item) => {
            try {
              if (item.type === "invoice") {
                await approveInvoice(item.id);
                results.success.push({
                  id: item.id,
                  number: item.number,
                  type: "Bill",
                });
              } else {
                await approveCredit(item.id);
                results.success.push({
                  id: item.id,
                  number: item.number,
                  type: "Credit",
                });
              }
            } catch (error) {
              const errorMsg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "Unknown error";
              results.failed.push({
                id: item.id,
                number: item.number,
                type: item.type === "invoice" ? "Bill" : "Credit",
                error: errorMsg,
              });
            }
          });

          await Promise.allSettled(promises);

          // Show summary
          const successCount = results.success.length;
          const failedCount = results.failed.length;

          if (successCount > 0) {
            message.success(
              `Successfully approved ${successCount} item${
                successCount > 1 ? "s" : ""
              }`
            );
          }
          if (failedCount > 0) {
            message.error(
              `Failed to approve ${failedCount} item${
                failedCount > 1 ? "s" : ""
              }`
            );
            console.error("Failed approvals:", results.failed);
          }

          // Clear selections
          setSelectedBillIds([]);
          setSelectedCreditIds([]);

          // Refresh data using handleRefresh
          setTimeout(() => {
            handleRefresh();
          }, 500);

          setBulkApproving(false);
        },
      });
    },
    [
      selectedBillIds,
      selectedCreditIds,
      data,
      creditNotesData,
      isEligibleForApproval,
      handleRefresh,
    ]
  );

  // Tab content components
  const DashboardTab = () => {
    const { start: periodStart, end: periodEnd } =
      getDateRangeFromFilter(dashboardDateFilter);

    return (
      <div>
        {/* Date Filter Controls */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                <strong>Period Filter:</strong>
                <Select
                  style={{ width: "100%" }}
                  value={dashboardDateFilter}
                  onChange={(value) => {
                    setDashboardDateFilter(value);
                    if (value !== "custom") {
                      setDashboardCustomRange(null);
                    }
                  }}
                >
                  <Option value="this_month">This Month</Option>
                  <Option value="last_month">Last Month</Option>
                  <Option value="this_year">This Year (YTD)</Option>
                  <Option value="custom">Custom Range</Option>
                </Select>
              </Space>
            </Col>
            {dashboardDateFilter === "custom" && (
              <Col xs={24} sm={12} md={8}>
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: "100%" }}
                >
                  <strong>Custom Date Range:</strong>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={dashboardCustomRange}
                    onChange={setDashboardCustomRange}
                    format="YYYY-MM-DD"
                  />
                </Space>
              </Col>
            )}
            <Col xs={24} sm={12} md={8}>
              <div style={{ color: "#666", fontSize: 12 }}>
                <strong>Selected Period:</strong>
                <br />
                {periodStart.format("MMM DD, YYYY")} -{" "}
                {periodEnd.format("MMM DD, YYYY")}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Unpaid Balance"
                value={
                  isConverted
                    ? statistics.totalOutstanding * exchangeRate
                    : statistics.totalOutstanding
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#1890ff" }}
                prefix={
                  isConverted ? (
                    <>
                      <DollarOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <DollarOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Current remaining (all time)
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Issued (Period)"
                value={
                  isConverted
                    ? (statistics.issuedInPeriod || 0) * exchangeRate
                    : statistics.issuedInPeriod || 0
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#722ed1" }}
                prefix={
                  isConverted ? (
                    <>
                      <FileTextOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <FileTextOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Bills issued in selected period
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Paid (Period)"
                value={
                  isConverted
                    ? statistics.paidThisMonth * exchangeRate
                    : statistics.paidThisMonth
                }
                precision={decimalPrecision}
                valueStyle={{ color: "#3f8600" }}
                prefix={
                  isConverted ? (
                    <>
                      <CheckCircleOutlined />{" "}
                      <SwapOutlined style={{ fontSize: 12, marginLeft: 4 }} />
                    </>
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
                suffix={displayCurrency}
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 8 }}>
                Payments made in period
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Bills"
                value={statistics.totalBills}
                precision={0}
                valueStyle={{ color: "#1890ff" }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Credit Notes"
                value={statistics.totalCreditNotes}
                precision={0}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Unallocated Credits"
                value={statistics.unallocatedCredits}
                precision={0}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Pending Payments"
                value={statistics.pendingPayments}
                precision={0}
                valueStyle={{ color: "#722ed1" }}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Recent Bills Table */}
        <Card title="Recent Bills" style={{ marginBottom: 16 }}>
          <div
            className="ag-theme-alpine"
            style={{ height: 300, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`recent-bills-${currencyRefreshKey}`}
              tableId="accounts-payable-recent-bills"
              columnDefs={billColumns}
              rowData={data.slice(0, 5)}
              defaultColDef={defaultColDefReadOnly}
              context={gridContext}
              domLayout="normal"
              enableCellTextSelection={true}
              ensureDomOrder={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "issueDate", sort: "desc" }],
                },
              }}
            />
          </div>
        </Card>

        {/* Recent Credit Notes Table */}
        <Card title="Recent Credit Notes">
          <div
            className="ag-theme-alpine"
            style={{ height: 300, width: "100%" }}
          >
            <AgGridWithColumnState
              key={`recent-credits-${currencyRefreshKey}`}
              tableId="accounts-payable-recent-credits"
              columnDefs={creditNoteColumns}
              rowData={creditNotesData.slice(0, 5)}
              defaultColDef={defaultColDefReadOnly}
              context={gridContext}
              domLayout="normal"
              enableCellTextSelection={true}
              ensureDomOrder={true}
              initialState={{
                sort: {
                  sortModel: [{ colId: "issueDate", sort: "desc" }],
                },
              }}
            />
          </div>
        </Card>
      </div>
    );
  };

  // CRITICAL: Memoize tab content to prevent unnecessary re-creation and glitching
  // These were previously function components that caused remounting on every parent render
  const BillsTabContent = useMemo(() => {
    // Determine if we have selected eligible bills
    const hasSelectedEligibleBills = eligibleBillCount > 0;
    const billButtonText = hasSelectedEligibleBills
      ? `Approve Selected (${eligibleBillCount})`
      : `Approve All (${totalApprovableBills})`;

    // Determine if we have selected eligible credits
    const hasSelectedEligibleCredits = eligibleCreditCount > 0;
    const creditButtonText = hasSelectedEligibleCredits
      ? `Approve Selected (${eligibleCreditCount})`
      : `Approve All (${totalApprovableCredits})`;

    // Only show approve button if there are items to approve
    const normalizedUserRole = userRole?.toLowerCase();
    const showBillApproveButton =
      normalizedUserRole === "management" &&
      (totalApprovableBills > 0 || hasSelectedEligibleBills);
    const showCreditApproveButton =
      normalizedUserRole === "management" &&
      (totalApprovableCredits > 0 || hasSelectedEligibleCredits);

    return (
      <div>
        {/* Bills Table */}
        <Card style={{ marginBottom: 16 }}>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4>
              Bills
              {isLoadingMoreBills && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    fontWeight: "normal",
                    color: "#1890ff",
                  }}
                >
                  <Spin size="small" style={{ marginRight: 4 }} />
                  Loading more...
                </span>
              )}
            </h4>
            <Space>
              {showBillApproveButton && (
                <Button
                  type="default"
                  onClick={() =>
                    handleBulkApprove("invoice", hasSelectedEligibleBills)
                  }
                  loading={bulkApproving}
                  disabled={bulkApproving}
                >
                  {billButtonText}
                </Button>
              )}
              <Button
                onClick={exportBillsToExcel}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                title="Export Bills to Excel"
              />
              <Button
                type="primary"
                onClick={() => setBatchUploadBillsVisible(true)}
                icon={<UploadOutlined />}
              >
                Upload Bills
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateBillModalVisible(true)}
              >
                Create Bill
              </Button>
            </Space>
          </div>

          <ResizableGridContainer defaultHeight={700} minHeight={300} maxHeight={1400}>
            <AgGridWithColumnState
              key={`bills-grid-${currencyRefreshKey}`}
              tableId="accounts-payable-bills"
              columnDefs={billColumns}
              rowData={data}
              defaultColDef={defaultColDef}
              context={gridContext}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              gridApiRef={billsGridApiRef}
              getRowId={(params) => String(params.data.id)}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              paginationPosition="top"
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={handleBillSelectionChanged}
              domLayout="normal"
              suppressScrollOnNewData={true}
              animateRows={false}
              rowBuffer={10}
            />
          </ResizableGridContainer>
          {selectedBillIds.length > 0 && (
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDeleteBills}
              >
                Delete Selected ({selectedBillIds.length})
              </Button>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setBatchEditModalVisible(true)}
              >
                Batch Edit ({selectedBillIds.length})
              </Button>
            </div>
          )}
        </Card>

        {/* Credit Notes Table */}
        <Card>
          <div
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4>
              Credit Notes
              {isLoadingMoreCredits && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 12,
                    fontWeight: "normal",
                    color: "#1890ff",
                  }}
                >
                  <Spin size="small" style={{ marginRight: 4 }} />
                  Loading more...
                </span>
              )}
            </h4>
            <Space>
              {showCreditApproveButton && (
                <Button
                  type="default"
                  onClick={() =>
                    handleBulkApprove("credit", hasSelectedEligibleCredits)
                  }
                  loading={bulkApproving}
                  disabled={bulkApproving}
                >
                  {creditButtonText}
                </Button>
              )}
              <Button
                onClick={exportCreditsToExcel}
                icon={
                  <ImageWithBasePath
                    src="assets/img/icons/xls.svg"
                    alt="Excel"
                    style={{ width: 16, height: 16 }}
                  />
                }
                title="Export Credit Notes to Excel"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateCreditModalVisible(true)}
              >
                Create Credit Note
              </Button>
            </Space>
          </div>

          <ResizableGridContainer defaultHeight={700} minHeight={300} maxHeight={1400}>
            <AgGridWithColumnState
              key={`credits-grid-${currencyRefreshKey}`}
              tableId="accounts-payable-credits"
              columnDefs={creditNoteColumns}
              rowData={creditNotesData}
              defaultColDef={defaultColDef}
              context={gridContext}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              gridApiRef={creditsGridApiRef}
              getRowId={(params) => String(params.data.id)}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100]}
              paginationPosition="top"
              stopEditingWhenCellsLoseFocus={true}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              onSelectionChanged={handleCreditSelectionChanged}
              domLayout="normal"
              suppressScrollOnNewData={true}
              animateRows={false}
              rowBuffer={10}
            />
          </ResizableGridContainer>
          {selectedCreditIds.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkDeleteCredits}
              >
                Delete Selected ({selectedCreditIds.length})
              </Button>
            </div>
          )}
        </Card>
      </div>
    );
  }, [
    data,
    creditNotesData,
    billColumns,
    creditNoteColumns,
    defaultColDef,
    gridContext,
    onBillsGridReady,
    onCreditsGridReady,
    onCellValueChanged,
    exportBillsToExcel,
    exportCreditsToExcel,
    showUploadModal,
    handleBulkApprove,
    bulkApproving,
    eligibleBillCount,
    eligibleCreditCount,
    totalApprovableBills,
    totalApprovableCredits,
    userRole,
    isLoadingMoreBills,
    isLoadingMoreCredits,
    selectedBillIds,
    selectedCreditIds,
    handleBulkDeleteBills,
    handleBulkDeleteCredits,
    handleBillSelectionChanged,
    handleCreditSelectionChanged,
  ]);

  const HistoryTabContent = useMemo(
    () => (
      <div>
        <Card title="Bills Action History">
          <BillHistory
            firstBrand={firstBrand}
            selectedBrands={selectedBrands}
          />
        </Card>
      </div>
    ),
    [firstBrand, selectedBrands]
  );

  const UpcomingBillsTabContent = useMemo(
    () => (
      <div>
        <UpcomingBills
          firstBrand={firstBrand}
          selectedBrands={selectedBrands}
        />
      </div>
    ),
    [firstBrand, selectedBrands]
  );

  // Handle tab change - save to localStorage
  const handleTabChange = (key) => {
    setActiveTab(key);
    visitedTabsRef.current.add(key);
    localStorage.setItem("accounts-payable-active-tab", key);
  };

  // Tab items configuration
  // CRITICAL: Using memoized content to prevent glitching from component recreation
  // Also using destroyInactiveTabPane=false and forceRender=true for the bills tab
  // to ensure the AG Grid stays mounted and preserves filter state between actions
  const tabItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      children: <DashboardTab />,
    },
    {
      key: "bills",
      label: "Bills and Credit Notes",
      children: BillsTabContent,
      forceRender: true, // Keep grid mounted to preserve filter state
    },
    {
      key: "history",
      label: "Bills History",
      children: HistoryTabContent,
    },
    {
      key: "upcoming",
      label: "Upcoming Bills",
      children: UpcomingBillsTabContent,
    },
  ];

  return (
    <div
      style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
    >
      <div style={{ padding: "16px", margin: "0" }}>
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              margin: "0 0 16px 0",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            Accounts Payable
          </h2>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          type="card"
          destroyInactiveTabPane={false}
        />
      </div>

      {/* Create/Edit Bill Modal - only mount when needed */}
      {createBillModalVisible && (
        <CreateInvoiceModal
          visible={createBillModalVisible}
          onCancel={() => {
            setCreateBillModalVisible(false);
            setEditingBillId(null);
          }}
          onSuccess={handleCreateBillSuccess}
          selectedBrand={firstBrand}
          invoiceId={editingBillId}
          defaultInvoiceType="bill"
        />
      )}

      {/* Batch Upload Bills Modal */}
      <BatchUploadBillsInvoices
        visible={batchUploadBillsVisible}
        onCancel={() => setBatchUploadBillsVisible(false)}
        onSuccess={handleRefresh}
        selectedBrand={firstBrand}
        invoiceType="bill"
      />

      {/* Batch Edit Modal */}
      <BatchEditModal
        visible={batchEditModalVisible}
        onCancel={() => setBatchEditModalVisible(false)}
        onSuccess={() => {
          setBatchEditModalVisible(false);
          setSelectedBillIds([]);
          handleRefresh();
        }}
        selectedIds={selectedBillIds}
        invoiceType="bill"
        currentBrand={firstBrand}
      />

      {/* Create/Edit Credit Modal */}
      <Modal
        title={editingCreditId ? "Edit Credit Note" : "Create Credit Note"}
        open={createCreditModalVisible}
        onCancel={() => {
          setCreateCreditModalVisible(false);
          setEditingCreditId(null);
        }}
        footer={null}
        width="95%"
        style={{ top: 20 }}
        zIndex={2000}
        destroyOnClose
      >
        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <CreateCredit
            selectedBrand={firstBrand}
            creditId={editingCreditId}
            onSuccess={handleCreateCreditSuccess}
            defaultCreditType="bill"
          />
        </div>
      </Modal>

      {/* Upload Bills Modal */}
      <Modal
        title="Upload Bills File"
        open={uploadModalVisible}
        onOk={handleUploadOk}
        onCancel={handleUploadCancel}
        confirmLoading={uploadLoading}
        okText="Upload"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="file"
            label="Excel or CSV File"
            rules={[{ required: true, message: "Please upload a file" }]}
          >
            <Upload.Dragger {...uploadProps} maxCount={1}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Only Excel (.xlsx, .xls) and CSV (.csv) files are supported
              </p>
              <p
                className="ant-upload-hint"
                style={{
                  marginTop: "8px",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                Uploading as: Bill
              </p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea
              rows={4}
              placeholder="Add any notes about this bills file"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Invoice View Modal */}
      <InvoiceViewModal
        visible={viewInvoiceModalVisible}
        onCancel={() => {
          setViewInvoiceModalVisible(false);
          setViewingInvoiceId(null);
          setViewingCreditId(null);
        }}
        invoiceId={viewingInvoiceId || viewingCreditId}
        invoiceType={viewingCreditId ? "credit" : "bill"}
        isCredit={!!viewingCreditId}
      />

      {/* Approval Modal */}
      <ApprovalModal
        visible={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          setSelectedItemForApproval(null);
        }}
        onSuccess={handleApprovalSuccess}
        item={selectedItemForApproval}
        itemType={approvalItemType}
        userRole={userRole}
      />

      {/* Allocations Modal */}
      <Modal
        title={`${
          selectedInvoiceForPayments?.allocations?.length || 0
        } Allocation${
          (selectedInvoiceForPayments?.allocations?.length || 0) > 1 ? "s" : ""
        }`}
        open={paymentsModalVisible}
        onCancel={() => {
          setPaymentsModalVisible(false);
          setSelectedInvoiceForPayments(null);
        }}
        footer={null}
        width={800}
      >
        {selectedInvoiceForPayments && (
          <div>
            <p style={{ marginBottom: 16, fontSize: 14 }}>
              <strong>
                Allocations for{" "}
                {selectedInvoiceForPayments.credit_type
                  ? "Credit Note"
                  : "Bill"}{" "}
                #{selectedInvoiceForPayments.number}
              </strong>
            </p>
            <Table
              dataSource={selectedInvoiceForPayments.allocations || []}
              rowKey="id"
              size="small"
              pagination={false}
              columns={[
                {
                  title: "Type",
                  dataIndex: "allocation_type",
                  key: "allocation_type",
                  width: 100,
                  render: (type) => {
                    const typeConfig = {
                      payment: { color: "blue", label: "Payment" },
                      credit: { color: "green", label: "Credit" },
                      cash: { color: "purple", label: "Cash" },
                      overpayment: { color: "orange", label: "Overpayment" },
                    };
                    const config = typeConfig[type] || {
                      color: "default",
                      label: type || "Payment",
                    };
                    return <Tag color={config.color}>{config.label}</Tag>;
                  },
                },
                {
                  title: "Description",
                  dataIndex: ["transaction", "description"],
                  key: "description",
                  render: (text, record) => {
                    // Handle different allocation types
                    let desc = text || record.description || "No description";

                    // For credit allocations, show the invoice number that the credit is allocated to
                    if (record.allocation_type === "credit") {
                      if (record.invoice_number) {
                        desc = `Invoice #${record.invoice_number}`;
                      } else if (record.invoice?.number) {
                        desc = `Invoice #${record.invoice.number}`;
                      } else if (record.credit) {
                        desc = `Credit Note #${
                          record.credit.number || record.credit.id
                        }`;
                      }
                    }
                    // For cash allocations, show cash number
                    else if (record.allocation_type === "cash" && record.cash) {
                      desc = `Cash Payment #${
                        record.cash.number || record.cash.id
                      }`;
                    }
                    // For overpayment, show overpayment info
                    else if (
                      record.allocation_type === "overpayment" &&
                      record.overpayment
                    ) {
                      desc = record.overpayment.description || "Overpayment";
                    }

                    // Make clickable for transactions
                    if (
                      record.allocation_type === "payment" &&
                      (record.transaction || record.transaction_id)
                    ) {
                      // Extract actual transaction ID - handle both object and primitive
                      const rawTransaction =
                        record.transaction_id || record.transaction;
                      const transactionId =
                        typeof rawTransaction === "object" &&
                        rawTransaction !== null
                          ? rawTransaction.id
                          : rawTransaction;

                      if (transactionId) {
                        return (
                          <a
                            href={`/accounting/transactions-allocations?transaction=${transactionId}`}
                            onClick={(e) => {
                              e.preventDefault();
                              // Use navigate for SPA navigation instead of full page reload
                              navigate(
                                `/accounting/transactions-allocations?transaction=${transactionId}`
                              );
                            }}
                            style={{ color: "#1890ff", cursor: "pointer" }}
                          >
                            {desc}
                          </a>
                        );
                      }
                    }
                    return desc;
                  },
                },
                {
                  title: "Amount",
                  dataIndex: "amount",
                  key: "amount",
                  width: 120,
                  render: (amount) =>
                    `${selectedInvoiceForPayments.currency || "$"} ${parseFloat(
                      amount || 0
                    ).toLocaleString("en-US", {
                      minimumFractionDigits: decimalPrecision,
                      maximumFractionDigits: decimalPrecision,
                    })}`,
                },
                {
                  title: "Date",
                  dataIndex: "allocation_date",
                  key: "allocation_date",
                  width: 120,
                  render: (date, record) => {
                    // Use transaction date for payment allocations, otherwise allocation date
                    const displayDate =
                      record.allocation_type === "payment" &&
                      record.transaction_date
                        ? record.transaction_date
                        : date;
                    return displayDate
                      ? dayjs(displayDate).format("MM/DD/YYYY")
                      : "N/A";
                  },
                },
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AccountsPayable;
